/* eslint-disable prettier/prettier */
/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/** 
  All of the routes for the Material Dashboard 2 React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that has other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

import { useEffect } from "react";
// react-router-dom components
import { Navigate } from "react-router-dom";

// Material Dashboard 2 React layouts
import Home from "layouts/home";
import CategoryProducts from "layouts/category-products";
import Cart from "layouts/cart";
import CheckoutConfirmation from "layouts/checkout-confirmation";
import MyOrders from "layouts/my-orders";
import Profile from "layouts/profile";
import SignIn from "layouts/authentication/sign-in";
import MobileSignIn from "layouts/authentication/mobile-sign-in";
import ForgotPassword from "layouts/authentication/reset-password/cover";
import TermsAndConditions from "layouts/authentication/terms-and-conditions";
import Endpoints from "layouts/tables/data";
import Roles from "layouts/roles";
import Application from "layouts/application";
import Members from "layouts/members";
import Organization from "layouts/organization";
import MemberRoles from "layouts/member-roles";
import EndpointRoles from "layouts/endpoint-roles";
import RefreshTokens from "layouts/refresh-tokens";
import ErrorLogs from "layouts/error-logs";
import { useMaterialUIController, clearCart } from "context";

// @mui icons
import Icon from "@mui/material/Icon";

const SignOut = () => {
  const [, dispatch] = useMaterialUIController();

  useEffect(() => {
    clearCart(dispatch);
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("isTocAccepted");
  }, [dispatch]);

  return <Navigate to="/" />;
};

const routes = [
  {
    key: "home",
    route: "/",
    public: true,
    component: <Home />,
  },
  {
    key: "category-products",
    route: "/category/:categorySlug",
    public: true,
    component: <CategoryProducts />,
  },
  {
    key: "products",
    route: "/products",
    public: true,
    component: <CategoryProducts />,
  },
  {
    key: "cart",
    route: "/cart",
    component: <Cart />,
  },
  {
    key: "profile",
    route: "/profile",
    component: <Profile />,
  },
  {
    key: "checkout-confirmation",
    route: "/checkout/confirmation",
    component: <CheckoutConfirmation />,
  },
  {
    key: "my-orders",
    route: "/my-orders",
    component: <MyOrders />,
  },
  {
    type: "collapse",
    name: "Endpoints",
    key: "endpoints",
    icon: <Icon fontSize="small">api</Icon>,
    route: "/endpoints",
    component: <Endpoints />,
  },
  {
    type: "collapse",
    name: "Roles",
    key: "roles",
    icon: <Icon fontSize="small">admin_panel_settings</Icon>,
    route: "/roles",
    component: <Roles />,
  },
  {
    type: "collapse",
    name: "Application",
    key: "application",
    icon: <Icon fontSize="small">apps</Icon>,
    route: "/application",
    component: <Application />,
  },
  {
    type: "collapse",
    name: "Members",
    key: "members",
    icon: <Icon fontSize="small">groups</Icon>,
    route: "/members",
    component: <Members />,
  },
  {
    type: "collapse",
    name: "Organization",
    key: "organization",
    icon: <Icon fontSize="small">apartment</Icon>,
    route: "/organization",
    component: <Organization />,
  },
  {
    type: "collapse",
    name: "Member Roles",
    key: "member-roles",
    icon: <Icon fontSize="small">badge</Icon>,
    route: "/member-roles",
    component: <MemberRoles />,
  },
  {
    type: "collapse",
    name: "Endpoint Roles",
    key: "endpoint-roles",
    icon: <Icon fontSize="small">hub</Icon>,
    route: "/endpoint-roles",
    component: <EndpointRoles />,
  },
  {
    type: "collapse",
    name: "Refresh Tokens",
    key: "refresh-tokens",
    icon: <Icon fontSize="small">token</Icon>,
    route: "/refresh-tokens",
    component: <RefreshTokens />,
  },
  {
    type: "collapse",
    name: "Error Logs",
    key: "error-logs",
    icon: <Icon fontSize="small">report_problem</Icon>,
    route: "/error-logs",
    component: <ErrorLogs />,
  },
  {
    key: "sign-in",
    route: "/authentication/sign-in",
    component: <SignIn />,
  },
  {
    key: "mobile-sign-in",
    route: "/authentication/mobile-sign-in",
    component: <MobileSignIn />,
  },
  {
    key: "terms-and-conditions",
    route: "/authentication/terms-and-conditions",
    component: <TermsAndConditions />,
  },
  {
    key: "forgot-password",
    route: "/authentication/forgot-password",
    component: <ForgotPassword />,
  },
  {
    type: "divider",
    key: "divider-logout",
  },
  {
    type: "collapse",
    name: "Log Out",
    key: "sign-out",
    icon: <Icon fontSize="small">logout</Icon>,
    route: "/authentication/sign-out",
    component: <SignOut />,
  },
];

export default routes;
